import { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Stethoscope, Badge, DollarSign, Calendar, FileText, Clock, Key } from 'lucide-react';
import { doctorsApi, Doctor, ScheduleEntry } from '../../lib/api';
import ResetPasswordModal from './ResetPasswordModal';

interface DoctorDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctorId: string | null;
}

const DoctorDetailsModal = ({ isOpen, onClose, doctorId }: DoctorDetailsModalProps) => {
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

  useEffect(() => {
    const fetchDoctor = async () => {
      if (!doctorId || !isOpen) {
        setDoctor(null);
        setSchedule([]);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const response = await doctorsApi.getDoctor(doctorId);
        if (response.data.success && response.data.data) {
          setDoctor(response.data.data);
          // Fetch schedule after doctor is loaded
          fetchSchedule(doctorId);
        } else {
          setError('Failed to fetch doctor details');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch doctor details');
      } finally {
        setLoading(false);
      }
    };

    const fetchSchedule = async (id: string) => {
      setScheduleLoading(true);
      try {
        const response = await doctorsApi.getSchedule(id);
        if (response.data.success && response.data.data) {
          setSchedule(response.data.data.schedule || []);
        }
      } catch (err: any) {
        console.error('Failed to fetch schedule:', err);
        // Don't show error for schedule, just keep empty
        setSchedule([]);
      } finally {
        setScheduleLoading(false);
      }
    };

    fetchDoctor();
  }, [doctorId, isOpen]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatSpecialization = (specialization: string) => {
    return specialization.replace(/([A-Z])/g, ' $1').trim();
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDayOfWeek = (day: string) => {
    return day.charAt(0) + day.slice(1).toLowerCase();
  };

  const getDayOrder = (day: string) => {
    const order = { 'MONDAY': 1, 'TUESDAY': 2, 'WEDNESDAY': 3, 'THURSDAY': 4, 'FRIDAY': 5, 'SATURDAY': 6, 'SUNDAY': 7 };
    return order[day as keyof typeof order] || 8;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-primary-50 to-blue-50">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <Stethoscope className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {loading ? 'Loading...' : doctor ? `Dr. ${doctor.user.firstName} ${doctor.user.lastName}` : 'Doctor Details'}
              </h2>
              <p className="text-sm text-gray-500">
                {doctor && `License: ${doctor.licenseNumber}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="space-y-6">
              {/* Loading skeleton */}
              <div className="animate-pulse">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                  <div className="space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-2">Error loading doctor details</div>
              <p className="text-gray-600 mb-4">{error}</p>
              <button onClick={onClose} className="btn-secondary">
                Close
              </button>
            </div>
          ) : doctor ? (
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <User className="h-5 w-5 text-gray-400 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Full Name</label>
                    <p className="mt-1 text-sm text-gray-900">
                      Dr. {doctor.user.firstName} {doctor.user.lastName}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email Address</label>
                    <div className="mt-1 flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-sm text-gray-900">{doctor.user.email}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone Number</label>
                    <div className="mt-1 flex items-center">
                      <Phone className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-sm text-gray-900">{doctor.user.phone}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        doctor.user.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {doctor.user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Stethoscope className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Professional Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Medical License Number</label>
                    <div className="mt-1 flex items-center">
                      <FileText className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-sm text-gray-900 font-mono">{doctor.licenseNumber}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Specialization</label>
                    <div className="mt-1">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        <Badge className="h-4 w-4 mr-1" />
                        {formatSpecialization(doctor.specialization)}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Department</label>
                    <div className="mt-1">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {doctor.department}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Consultation Fee</label>
                    <div className="mt-1 flex items-center">
                      <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-sm text-gray-900 font-semibold">
                        {formatCurrency(doctor.consultationFee)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Calendar className="h-5 w-5 text-green-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Statistics & Activity</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {doctor._count?.appointments || 0}
                    </div>
                    <div className="text-sm text-gray-500">Total Appointments</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatDate(doctor.createdAt)}
                    </div>
                    <div className="text-sm text-gray-500">Joined Date</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {formatDate(doctor.updatedAt)}
                    </div>
                    <div className="text-sm text-gray-500">Last Updated</div>
                  </div>
                </div>
              </div>

              {/* Schedule Information */}
              <div className="bg-indigo-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Clock className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Schedule & Availability</h3>
                </div>
                
                {scheduleLoading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : schedule.length > 0 ? (
                  <div className="space-y-3">
                    {schedule
                      .sort((a, b) => getDayOrder(a.dayOfWeek) - getDayOrder(b.dayOfWeek))
                      .map((entry, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                            <span className="font-medium text-gray-900">
                              {formatDayOfWeek(entry.dayOfWeek)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-600">
                              {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              entry.isAvailable 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {entry.isAvailable ? 'Available' : 'Unavailable'}
                            </span>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h4 className="text-lg font-medium text-gray-600 mb-2">
                      No Schedule Set
                    </h4>
                    <p className="text-gray-500">
                      This doctor hasn't configured their availability schedule yet.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-500">No doctor data available</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <button
            onClick={() => setShowResetPasswordModal(true)}
            className="btn-secondary bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
            disabled={!doctor}
          >
            <Key className="h-4 w-4 mr-2" />
            Reset Password
          </button>
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        doctorId={doctorId}
        doctorName={doctor ? `${doctor.user.firstName} ${doctor.user.lastName}` : ''}
      />
    </div>
  );
};

export default DoctorDetailsModal; 